import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { CmsLinkComponent, CmsNavigationNode, CmsService, WindowRef } from '@spartacus/core';
import { CmsComponentData, ICON_TYPE } from '@spartacus/storefront';
import { combineLatest, map, Observable, of, Subject, switchMap, takeUntil } from 'rxjs';
import { SubmenuStateService } from './submenu-state.service';

export interface NavigationComponent {
  uid: string;
  navigationNode: CmsNavigationNode;
}

function validNavigationNode(node: CmsNavigationNode): boolean {
  return !!node.entries && node.entries.length > 0;
}
type MenuItemWithComponents = CmsNavigationNode & {
  uid: string;
  entryComponents: CmsLinkComponent[];
  children?: MenuItemWithComponents[];
};
@Component({
  selector: 'app-navigation',
  templateUrl: 'navigation.component.html',
  standalone: false,
})
export class NavigationComponent implements AfterViewInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  data$: Observable<NavigationComponent> = this.componentData.data$;
  iconTypes = ICON_TYPE;

  menuItems$: Observable<MenuItemWithComponents[]> = this.data$.pipe(
    map((data) => data.navigationNode?.children || []),
    map((children) => children.filter(validNavigationNode)),
    switchMap((children) => combineLatest(children.map((child) => this.mapNodeWithComponents(child)))),
    takeUntil(this.destroy$)
  );

  // Expose submenu state observables
  openMenuItemUid$ = this.submenuStateService.openMenuItemUid$;
  isMobile$ = this.submenuStateService.isMobile$;

  @ViewChildren('disclosureButton') disclosureButtons!: QueryList<ElementRef>;

  constructor(
    private componentData: CmsComponentData<NavigationComponent>,
    private cmsService: CmsService,
    private elementRef: ElementRef,
    private windowRef: WindowRef,
    private cdr: ChangeDetectorRef,
    private submenuStateService: SubmenuStateService
  ) {}

  ngAfterViewInit() {
    // Subscribe to submenu state changes to trigger change detection
    this.submenuStateService.openMenuItemUid$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.detectChanges();
    });
  }

  private mapNodeWithComponents(node: CmsNavigationNode): Observable<MenuItemWithComponents> {
    const filteredChildren = (node.children || []).filter(validNavigationNode);
    return combineLatest([
      node.entries && node.entries.length
        ? combineLatest(node.entries.map((entry) => this.cmsService.getComponentData<CmsLinkComponent>(entry.itemId!)))
        : of([] as CmsLinkComponent[]),
      filteredChildren.length
        ? combineLatest(filteredChildren.map((child) => this.mapNodeWithComponents(child)))
        : of([] as MenuItemWithComponents[]),
    ]).pipe(
      map(([entryComponents, children]) => ({
        ...node,
        uid: node.uid!,
        entryComponents,
        children: children.length ? children : undefined,
      }))
    );
  }

  isSubmenuOpen(menuItemUid: string): boolean {
    return this.submenuStateService.isSubmenuOpen(menuItemUid);
  }

  toggleSubmenu(menuItemUid: string, event?: MouseEvent, autofocusFirstItem: boolean = true): void {
    event?.stopPropagation();
    this.submenuStateService.toggleSubmenu(menuItemUid);

    if (autofocusFirstItem) {
      this.openMenuItemUid$.pipe(takeUntil(this.destroy$)).subscribe((openUid) => {
        if (openUid === menuItemUid) {
          setTimeout(() => {
            const submenuElement = this.elementRef.nativeElement.querySelector(`#submenu-${menuItemUid}`);
            if (submenuElement) {
              const focusable = submenuElement.querySelector(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
              ) as HTMLElement | null;
              focusable?.focus();
            }
          }, 0);
        }
      });
    }
  }

  isExternal(entry: any): boolean {
    const v = entry?.external;
    return v === true || (typeof v === 'string' && v.trim().toLowerCase() === 'true');
  }

  normalizeUrl(url: string | undefined | null): string {
    const str = (url ?? '').trim();
    if (!str) return '';
    // Ensure the protocol has two slashes
    return str.replace(/^https:\/*/i, 'https://').replace(/^http:\/*/i, 'http://');
  }

  setUrl(entry: any) {
    return this.normalizeUrl(entry?.locUrl);
  }

  getTarget(entry: CmsLinkComponent): string {
    /**
     * Returns `_blank` to force opening the link in a new window whenever the
     * `entry.target` flag is set to `true`.
     */
    return entry.target === 'true' || entry.target === true ? '_blank' : '_self';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.submenuStateService.isMobile) {
      return;
    }

    if (
      this.submenuStateService.currentOpenMenuItemUid &&
      this.submenuStateService.isClickOutside(event, this.elementRef.nativeElement)
    ) {
      this.submenuStateService.forceCloseAllMenus();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.submenuStateService.currentOpenMenuItemUid) {
      this.closeOpenMenuAndFocusButton();
    }
  }

  private closeOpenMenuAndFocusButton(): void {
    const previouslyOpenUid = this.submenuStateService.currentOpenMenuItemUid;
    this.submenuStateService.forceCloseAllMenus();
    if (previouslyOpenUid) {
      setTimeout(() => {
        const buttonToFocus = this.disclosureButtons.find(
          (btn) => btn.nativeElement.getAttribute('aria-controls') === `submenu-${previouslyOpenUid}`
        );
        buttonToFocus?.nativeElement.focus();
      });
    }
  }

  onSubmenuFocusOut(event: FocusEvent, menuItemUid: string): void {
    if (this.submenuStateService.isMobile) {
      return;
    }

    if (this.submenuStateService.isSubmenuOpen(menuItemUid)) {
      const submenuElement = this.elementRef.nativeElement.querySelector(`#submenu-${menuItemUid}`);
      if (submenuElement && !submenuElement.contains(event.relatedTarget as Node | null)) {
        this.submenuStateService.forceCloseAllMenus();
      }
    }
  }

  clearFocus(): void {
    if (!this.windowRef.isBrowser()) {
      return;
    }

    const activeElement = document.activeElement;
    if (activeElement && activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
    this.submenuStateService.forceCloseAllMenus();
  }

  handleItemMouseEnter(menuItemUid: string, event: MouseEvent): void {
    this.submenuStateService.handleItemMouseEnter(menuItemUid);
  }

  handleItemMouseLeave(menuItemUid: string, event: MouseEvent): void {
    this.submenuStateService.handleItemMouseLeave(menuItemUid);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
